/**
 * AlwaysZeroConfirmDialog: the one beat before any SKU is set to Always 0
 * from a search row, a whole product or the list's selection.
 *
 * Always 0 writes 0 to a live storefront on the next push and, by design,
 * skips the Mass Out-of-Stock Guard, the Approval Queue and Delta Block for
 * those SKUs, because 0 is what the operator asked for. Only the big-batch
 * pause still holds a very large batch for approval. A base garment fans out
 * to every design printed on it, so one row can be thousands of Shopify
 * variants. The dialog leads with that number, so the impact is read before
 * anything is written. Skip never asks: it cannot zero a storefront.
 *
 * The impact body (`AlwaysZeroImpact`) is exported, and the title lives in
 * the utils (`alwaysZeroTitle`), so the paste dialog's review shows the same
 * words when its rule is Always 0: one review that commits directly, never a
 * dialog on top of a dialog (DESIGN.md 10).
 *
 * The confirm wears the Always 0 tone with its Ban glyph, the same as the
 * Always 0 buttons that open it, so the button says what it does.
 */
import { Ban, Clock, Info, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button-variants';
import { NoteLine } from '@/components/shared/NoteLine';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { alwaysZeroTitle, type AlwaysZeroSummary } from '../utils/push-sku-rules';
import { ALWAYS_ZERO_BUTTON_CLASS } from './push-rule-styles';

/** The Always 0 commit, as an AlertDialogAction (which starts from the default button). */
const ALWAYS_ZERO_COMMIT_CLASS = cn(buttonVariants({ variant: 'outline' }), 'gap-1.5', ALWAYS_ZERO_BUTTON_CLASS);

interface AlwaysZeroImpactProps {
  /** null while the preview is still running. */
  summary: AlwaysZeroSummary | null;
  /** The preview failed: the count is known, the Shopify side is not. */
  previewFailed: boolean;
  /** The batch is past the big-batch pause: the push will wait for approval. */
  willPause: boolean;
  /** What the loading state says ("Checking these SKUs...", "Finding this product's SKUs..."). */
  loadingLabel?: string;
  className?: string;
}

/** What setting these SKUs to Always 0 does, numbers first. */
export function AlwaysZeroImpact({ summary, previewFailed, willPause, loadingLabel, className }: AlwaysZeroImpactProps) {
  const { t } = useTranslation('inventory');

  if (!summary && !previewFailed) {
    return (
      <div className={cn('space-y-2', className)} aria-busy="true">
        <span role="status" className="sr-only">
          {loadingLabel ?? t('stockSync.pushRules.confirm.checking')}
        </span>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (summary && summary.skuCount === 0) {
    return (
      <div className={cn('text-sm text-foreground', className)}>
        <p>{t('stockSync.pushRules.confirm.nothingToChange')}</p>
      </div>
    );
  }

  const variants = summary?.variantCount ?? null;
  return (
    <div className={cn('space-y-2 text-sm text-foreground', className)}>
      {summary && variants !== null && (
        <p className="font-medium">
          {summary.variantCountPartial
            ? t('stockSync.pushRules.confirm.statAtLeast', { count: variants, variants: variants.toLocaleString() })
            : t('stockSync.pushRules.confirm.stat', { count: variants, variants: variants.toLocaleString() })}
        </p>
      )}
      {summary && summary.baseGarmentCount > 0 && (
        <p className={cn(variants === null && 'font-medium')}>
          {t('stockSync.pushRules.confirm.baseGarments', { count: summary.baseGarmentCount })}
        </p>
      )}
      <p>{t('stockSync.pushRules.confirm.lead')}</p>
      <p>{t('stockSync.pushRules.confirm.guards')}</p>
      {willPause && <WarningNotice>{t('stockSync.pushRules.confirm.willPause')}</WarningNotice>}
      {summary?.anyCacheCold && (
        <NoteLine icon={Clock} tone="warning" className="text-sm">
          {t('stockSync.pushRules.confirm.cacheCold', { count: summary.baseGarmentCount })}
        </NoteLine>
      )}
      {previewFailed && (
        <NoteLine icon={Info} className="text-sm">
          {t('stockSync.pushRules.confirm.previewFailed')}
        </NoteLine>
      )}
      <NoteLine icon={Info} className="text-sm">
        {t('stockSync.pushRules.confirm.keepSelling')}
      </NoteLine>
    </div>
  );
}

interface AlwaysZeroConfirmDialogProps {
  open: boolean;
  summary: AlwaysZeroSummary | null;
  previewFailed: boolean;
  willPause: boolean;
  /** The count shown before the preview lands (or when it failed). */
  requestedCount: number;
  loadingLabel?: string;
  applying: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Where focus goes on close; leave it to Radix (back to the trigger) unless the trigger is gone. */
  onCloseAutoFocus?: (event: Event) => void;
}

export function AlwaysZeroConfirmDialog({
  open,
  summary,
  previewFailed,
  willPause,
  requestedCount,
  loadingLabel,
  applying,
  onConfirm,
  onCancel,
  onCloseAutoFocus,
}: AlwaysZeroConfirmDialogProps) {
  const { t } = useTranslation('inventory');
  const ready = summary !== null || previewFailed;
  // A failed preview still knows the ids it was asked about; a product's ids
  // come only from the preview, so a failed product preview cannot go ahead.
  const canConfirm = ready && !applying && (summary === null ? requestedCount > 0 : summary.skuCount > 0);

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // Escape and the overlay do nothing while the write is in flight, so
        // the dialog never closes over an unresolved change.
        if (!next && !applying) onCancel();
      }}
    >
      <AlertDialogContent onCloseAutoFocus={onCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{alwaysZeroTitle(t, summary, requestedCount)}</AlertDialogTitle>
          {/* asChild: everything a screen reader gets on open lives in the
              description (Radix wires only Title + Description), so the whole
              impact is read, not just its first line. */}
          <AlertDialogDescription asChild>
            <div>
              <AlwaysZeroImpact
                summary={summary}
                previewFailed={previewFailed}
                willPause={willPause}
                loadingLabel={loadingLabel}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={applying}>{t('stockSync.pushRules.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            aria-busy={applying || undefined}
            className={ALWAYS_ZERO_COMMIT_CLASS}
            onClick={(event) => {
              // Stay open until the write settles; the caller closes it.
              event.preventDefault();
              if (canConfirm) onConfirm();
            }}
          >
            {applying ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <Ban className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {applying ? t('stockSync.pushRules.confirm.applying') : t('stockSync.pushRules.confirm.action')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
